import React, { FunctionComponent } from 'react';

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { TRANSLATION_NAMESPACE, COMPLETION_STATUS } from 'directory/listings/notification-modals/constants';
import { FinalSummaryContentProps } from 'directory/listings/notification-modals/types';
import 'directory/listings/notification-modals/components/FinalSummaryContent/FinalSummaryContent.scss';

const DownloadIconSVG = () => (
    <svg
        width="1rem"
        height="1rem"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="download-icon mr-2"
    >
        <path
            d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M4.66406 6.66406L7.9974 9.9974L11.3307 6.66406"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M8 10V2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const CheckCircleIconSVG = ({ color }: { color: string }) => (
    <svg width="3rem" height="3rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke={color} fill={color} strokeWidth="1.5" />
        <path d="M8 12L11 15L16 9" stroke="#ffffff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const FinalSummaryContent: FunctionComponent<FinalSummaryContentProps> = ({
    status,
    syncedCount,
    totalCount,
    timeTaken,
    onDownloadErrors,
}) => {
    const { t } = useTranslation(TRANSLATION_NAMESPACE);

    const getIconConfig = () => {
        switch (status) {
            case COMPLETION_STATUS.FULL_SUCCESS:
                return {
                    customSVG: <CheckCircleIconSVG color="#40c695" />,
                    className: 'final-summary-content__icon--success',
                    bgColor: '#E5FCEF',
                };
            case COMPLETION_STATUS.PARTIAL_SUCCESS:
                return {
                    customSVG: <CheckCircleIconSVG color="#FFCD36" />,
                    className: 'final-summary-content__icon--partial',
                    bgColor: '#FDF7E6',
                };
            case COMPLETION_STATUS.FULL_ERROR:
                return {
                    icon: faExclamationTriangle,
                    className: 'final-summary-content__icon--error',
                    bgColor: '#FEF2F2',
                    iconColor: '#F35C3E',
                };
            default:
                return {
                    customSVG: <CheckCircleIconSVG color="#10b981" />,
                    className: '',
                    bgColor: '#d1fae5',
                };
        }
    };

    const getContent = () => {
        switch (status) {
            case COMPLETION_STATUS.FULL_SUCCESS:
                return {
                    title: t('PROFILES_UPDATED_SUCCESS_TITLE'),
                    description: t('PROFILES_UPDATED_DESC'),
                    showStats: true,
                    statsBgColor: '#ECFDF5',
                };
            case COMPLETION_STATUS.PARTIAL_SUCCESS:
                return {
                    title: t('PROFILES_PARTIALLY_UPDATED_TITLE'),
                    description: t('PROFILES_UPDATED_DESC'),
                    showStats: true,
                    statsBgColor: '#FDF7E6',
                };
            case COMPLETION_STATUS.FULL_ERROR:
                return {
                    title: t('PROFILES_UPDATE_FAILED_TITLE'),
                    description: t('PROFILES_UPDATE_FAILED_DESC'),
                    showStats: false,
                    statsBgColor: '',
                };
            default:
                return {
                    title: '',
                    description: '',
                    showStats: false,
                    statsBgColor: '',
                };
        }
    };

    const iconConfig = getIconConfig();
    const content = getContent();

    return (
        <div className="final-summary-content">
            <div className="final-summary-content__icon-wrapper" style={{ backgroundColor: iconConfig.bgColor }}>
                {iconConfig.customSVG || (
                    <FontAwesomeIcon
                        icon={iconConfig.icon}
                        className={`final-summary-content__icon ${iconConfig.className}`}
                        style={{ color: iconConfig.iconColor }}
                    />
                )}
            </div>

            <div className="final-summary-content__title">{content.title}</div>

            <div className="final-summary-content__description">{content.description}</div>

            {content.showStats && (
                <div className="final-summary-content__stats" style={{ backgroundColor: content.statsBgColor }}>
                    <div className="final-summary-content__stat-item">
                        <div className="final-summary-content__stat-value">
                            {syncedCount} / {totalCount}
                        </div>
                        <div className="final-summary-content__stat-label">{t('SYNCED_PROFILES')}</div>
                    </div>
                    <div className="final-summary-content__stat-item">
                        <div className="final-summary-content__stat-value">{timeTaken}</div>
                        <div className="final-summary-content__stat-label">{t('TIME_TAKEN')}</div>
                    </div>
                </div>
            )}

            <div className="final-summary-content__footer">
                {(status === COMPLETION_STATUS.PARTIAL_SUCCESS || status === COMPLETION_STATUS.FULL_ERROR) &&
                    onDownloadErrors && (
                        <Button
                            variant="outline-primary"
                            className="final-summary-content__download-btn"
                            onClick={onDownloadErrors}
                        >
                            <DownloadIconSVG />
                            {t('DOWNLOAD_ERROR_FILE')}
                        </Button>
                    )}

                <a
                    className={
                        status === COMPLETION_STATUS.FULL_SUCCESS
                            ? 'rounded-btn outline-btn-color final-summary-content__view-success-btn'
                            : 'final-summary-content__view-btn'
                    }
                    href="/listings/profiles"
                    rel="noreferrer"
                >
                    {t('REVIEW_LISTING_PROFILES')}
                </a>
            </div>
        </div>
    );
};
